import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import { brand } from '@/shared/config/brand';
import { legalConfig } from '@/shared/config/legal';
import { LegalPageLayout, LegalSection } from '../components/LegalPageLayout';

function ContactEmail() {
  return (
    <a href={`mailto:${legalConfig.supportEmail}`}>{legalConfig.supportEmail}</a>
  );
}

/** Product brand name used in legal / A2P copy (must match SMS sender branding). */
const productName = brand.uiName;

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title={`${productName} Privacy Policy`}
      metaTitle={`${productName} Privacy Policy`}
      metaDescription={`Learn how ${productName} collects, uses, protects, and manages personal information, including information used for account authentication and SMS verification.`}
    >
      <Typography component="p">
        This Privacy Policy explains how {productName} (&quot;{productName},&quot; &quot;we,&quot;
        &quot;us,&quot; or &quot;our&quot;) collects, uses, shares, and protects personal information
        when you use our website at{' '}
        <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a> and related services
        (collectively, the &quot;Services&quot;).
      </Typography>
      <Typography component="p">
        {productName} is operated by {legalConfig.legalEntityName} and connects travelers with
        verified local guides. Please read this policy carefully. If you do not agree with this
        policy, please do not use the Services.
      </Typography>
      <Typography component="p">
        Operated by: {legalConfig.legalEntityName}
        <br />
        Business address: {legalConfig.businessAddress}
        <br />
        Contact: <ContactEmail />
      </Typography>

      <LegalSection id="information-we-collect" title="1. Information We Collect">
        <Typography component="p">
          Depending on how you use the Services, we may collect the following categories of
          information:
        </Typography>
        <Typography component="h3">Account and identity information</Typography>
        <ul>
          <li>Name (for example, first and last name)</li>
          <li>Email address</li>
          <li>Mobile / phone number</li>
          <li>Account credentials and authentication-related information</li>
          <li>Profile information you choose to provide (such as display name, country, or avatar)</li>
          <li>
            For Google Sign-In users: limited profile information provided by Google (such as name,
            email, and profile image), subject to your Google account settings
          </li>
          <li>
            Emergency contact details you choose to provide for trip safety (name and phone number)
          </li>
        </ul>
        <Typography component="h3">Guide and onboarding information</Typography>
        <ul>
          <li>
            Guide application and onboarding details (for example, experience, destinations,
            languages, pricing, availability, and related profile content)
          </li>
          <li>
            Identity or verification documents and related images you upload as part of guide
            verification (processed via our file-storage provider)
          </li>
          <li>Emergency or social contact details you choose to provide during onboarding</li>
        </ul>
        <Typography component="h3">Booking, request, and transaction information</Typography>
        <ul>
          <li>Guide connection / request details</li>
          <li>Booking details (dates, pricing selections, status, and related communications)</li>
          <li>
            Payment-related information processed through our payment provider (Cashfree), such as
            order references and payment status. Card details are handled by the payment provider
            according to its own terms and policies
          </li>
          <li>Reviews and ratings you submit</li>
        </ul>
        <Typography component="h3">Device, usage, and technical information</Typography>
        <ul>
          <li>Device and browser information</li>
          <li>IP address and related security / session metadata</li>
          <li>
            Local storage data used to keep you signed in and to support authentication flows (for
            example, access tokens and temporary OTP phone state)
          </li>
          <li>
            Analytics information collected through Google Analytics cookies and similar
            technologies used on our website
          </li>
        </ul>
        <Typography component="p">
          We collect information you provide directly, information generated through your use of the
          Services, and information from service providers that help us operate authentication,
          payments, hosting, analytics, and messaging.
        </Typography>
      </LegalSection>

      <LegalSection id="sms-mobile" title="2. SMS and Mobile Communications">
        <Typography component="p">
          {productName} collects a mobile phone number for account authentication and verification.
          When you initiate authentication, login, or verification and request a one-time verification
          code (OTP)—for example through our phone authentication flow at{' '}
          <a href={`${legalConfig.websiteUrl}/auth/phone?intent=guide`}>
            {legalConfig.websiteUrl}/auth/phone?intent=guide
          </a>
          —{productName} sends a one-time verification code by SMS.
        </Typography>
        <Typography component="p">
          These SMS messages are transactional / security messages used solely for authentication and
          verification. They are not marketing or promotional messages.
        </Typography>
        <Typography component="p">
          SMS consent is obtained through the {productName} phone authentication flow. Before an OTP
          SMS is sent, you must actively consent by selecting an SMS consent checkbox that is
          unchecked by default. This SMS consent is separate from acceptance of the Terms &amp;
          Conditions and this Privacy Policy. The phone authentication page includes a mobile phone
          number field, the SMS consent checkbox and disclosure, links to this Privacy Policy and the
          Terms &amp; Conditions, and an action to request an OTP. There is no SMS keyword opt-in
          (such as texting START, YES, or UNSTOP).
        </Typography>
        <ul>
          <li>
            Message frequency varies based on user-initiated authentication and verification
            activity.
          </li>
          <li>Message and data rates may apply.</li>
          <li>
            Mobile phone numbers, mobile information, and SMS opt-in/consent information are not sold,
            rented, or shared with third parties or affiliates for their own marketing or promotional
            purposes. We may share this information with service providers only as necessary to
            provide authentication, SMS delivery, security, and other services on our behalf.
          </li>
          <li>
            Such information may be shared with service providers only when necessary to provide
            authentication, SMS delivery, security, hosting, or related services on behalf of{' '}
            {productName} / {legalConfig.legalEntityName}. Twilio may be used as the SMS delivery
            provider.
          </li>
          <li>
            You may reply STOP to opt out of further SMS messages and HELP for help. You may also
            contact us regarding SMS or privacy questions at <ContactEmail />.
          </li>
        </ul>
        <Typography component="p">
          Verification codes (OTPs) expire after the configured period (currently{' '}
          {legalConfig.otpExpiryMinutes} minutes by default). Never share your OTP with anyone.
        </Typography>
      </LegalSection>

      <LegalSection id="how-we-use" title="3. How We Use Information">
        <Typography component="p">We use personal information to:</Typography>
        <ul>
          <li>Create and manage accounts</li>
          <li>Authenticate users and verify phone numbers via OTP SMS</li>
          <li>Provide marketplace features for travelers and guides</li>
          <li>Process bookings, payments, settlements, and related support</li>
          <li>Communicate about account, booking, and security matters</li>
          <li>Improve safety, prevent fraud, and enforce our Terms</li>
          <li>Analyze website usage to improve the Services</li>
          <li>Comply with legal obligations and respond to lawful requests</li>
        </ul>
      </LegalSection>

      <LegalSection id="third-parties" title="4. Third-Party Service Providers">
        <Typography component="p">
          We use trusted third-party service providers to operate the Services. These providers may
          process personal information only as necessary to perform services on our behalf, such as:
        </Typography>
        <ul>
          <li>
            <strong>Twilio</strong> — delivery of authentication / OTP SMS messages
          </li>
          <li>
            <strong>Google</strong> — Google Sign-In authentication and Google Analytics
          </li>
          <li>
            <strong>Cloudinary</strong> — image and document storage for profiles and verification
            uploads
          </li>
          <li>
            <strong>Cashfree</strong> — payment processing for bookings and related transactions
          </li>
          <li>Hosting, infrastructure, and database providers used to run the application</li>
        </ul>
        <Typography component="p">
          We do not sell personal information. Mobile phone numbers, mobile information, and SMS
          opt-in/consent information are not sold, rented, or shared with third parties or affiliates
          for their own marketing or promotional purposes. Service providers are expected to protect
          information and use it only to provide their services to us.
        </Typography>
      </LegalSection>

      <LegalSection id="security" title="5. Data Security">
        <Typography component="p">
          We implement reasonable administrative, technical, and organizational safeguards designed
          to protect personal information, including access controls, encrypted transport where
          applicable, hashed OTP storage, and session-based authentication. No method of
          transmission or storage is completely secure, and we cannot guarantee absolute security.
        </Typography>
      </LegalSection>

      <LegalSection id="retention" title="6. Data Retention">
        <Typography component="p">
          We retain personal information for as long as needed to provide the Services, maintain
          accounts, comply with legal and financial recordkeeping obligations, resolve disputes, and
          enforce our agreements. OTP records are retained only for a limited verification window and
          are removed according to our authentication design. Exact retention periods may vary by
          data category and legal requirements.
        </Typography>
      </LegalSection>

      <LegalSection id="your-choices" title="7. Your Choices and Requests">
        <Typography component="p">
          Depending on your account and applicable law, you may be able to:
        </Typography>
        <ul>
          <li>Access or review certain account information in your profile</li>
          <li>Correct or update account details</li>
          <li>Request deletion of your account or certain personal information</li>
          <li>Contact us with privacy or SMS-related questions</li>
        </ul>
        <Typography component="p">
          To make a request, contact us at <ContactEmail />. We may need to verify your identity
          before completing certain requests.
        </Typography>
      </LegalSection>

      <LegalSection id="cookies" title="8. Cookies and Local Storage">
        <Typography component="p">
          The Services use cookies and similar technologies, including Google Analytics cookies, to
          understand site usage. The Services also use browser local storage and session storage for
          authentication tokens, session identifiers, and temporary authentication state (such as the
          phone number used during an OTP flow). You can control cookies through your browser
          settings; disabling certain technologies may affect site functionality.
        </Typography>
      </LegalSection>

      <LegalSection id="children" title="9. Children">
        <Typography component="p">
          The Services are intended for users who can lawfully create an account and use marketplace
          features. We do not knowingly collect personal information from children for the purpose of
          creating accounts. If you believe a child has provided personal information, contact us so
          we can take appropriate action.
        </Typography>
      </LegalSection>

      <LegalSection id="changes" title="10. Changes to This Privacy Policy">
        <Typography component="p">
          We may update this Privacy Policy from time to time. The effective date at the top of this
          page will be revised when changes are posted. Continued use of the Services after an update
          means you acknowledge the revised policy.
        </Typography>
      </LegalSection>

      <LegalSection id="contact" title="11. Contact">
        <Typography component="p">
          For privacy questions, SMS consent questions, or data requests, contact:
        </Typography>
        <Typography component="p">
          {productName}
          <br />
          Operated by: {legalConfig.legalEntityName}
          <br />
          Website:{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a>
          <br />
          Email: <ContactEmail />
          <br />
          Address: {legalConfig.businessAddress}
        </Typography>
        <Typography component="p">
          Related documents:{' '}
          <RouterLink to={legalConfig.termsPath}>Terms &amp; Conditions</RouterLink>
        </Typography>
      </LegalSection>
    </LegalPageLayout>
  );
}
